import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import classNames from 'classnames';
import {CustomGallery, DefaultLayout, Item, useGallery} from 'react-photoswipe-gallery';
import PhotoSwipeUI from 'photoswipe/dist/photoswipe-ui-default';
import ViewGridIcon from '@heroicons/react/solid/ViewGridIcon';
import ArrowNarrowLeftIcon from '@heroicons/react/solid/ArrowNarrowLeftIcon';
import ArrowNarrowRightIcon from '@heroicons/react/solid/ArrowNarrowRightIcon';
import {WebsiteContext} from '../index';
import useCdn from '../hooks/cdn-hook';
import useGalleryOverlaySession from '../hooks/gallery-overlay-session';
import {getGalleryCategoryId, getGalleryImage} from '../../../forms/src/controls/gallery-items';
import {Caption, getCaption} from '../../../forms/src/controls/gallery-caption';
import ModalDialog from './modal-dialog';
import './gallery-carousel.css';

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const columnsForWidth = width => width >= 600 ? 4 : width >= 430 ? 3 : 2;
const imageDimensions = image => image.naturalWidth > 0 && image.naturalHeight > 0
    ? {width: 1680, height: Math.round(1680 * image.naturalHeight / image.naturalWidth)} : null;

function createGalleryUi(dimensions) {
    return function GalleryUi(instance, framework) {
        const pending = new Map();
        let refreshing = false;
        const refreshCurrent = () => {
            if (refreshing || !instance.currItem?.needsUpdate) return;
            refreshing = true;
            try { instance.updateSize(true); } finally { refreshing = false; }
        };
        instance.listen('gettingData', (index, item) => {
            if (pending.has(item) || !item.src) return;
            const original = item.src;
            const known = dimensions.get(original);
            if (known) {
                item.w = known.width;
                item.h = known.height;
                return;
            }
            // PhotoSwipe 4 needs dimensions before it lays out even a cached image.
            const image = new window.Image();
            pending.set(item, image);
            item.src = undefined;
            item.html = '<div role="status" class="text-center text-white p-6">Loading Image...</div>';
            const ready = () => {
                const size = imageDimensions(image) || {width: 1680, height: 1680};
                dimensions.set(original, size);
                pending.delete(item);
                image.onload = image.onerror = null;
                item.src = original;
                item.w = size.width;
                item.h = size.height;
                delete item.html;
                item.needsUpdate = true;
                refreshCurrent();
            };
            image.onload = image.onerror = ready;
            image.src = original;
        });
        const updateItemControls = () => {
            const note = !!instance.currItem?.galleryNote;
            instance.template.classList.toggle('gallery-viewer-note-active', note);
            const zoom = instance.template.querySelector('.pswp__button--zoom');
            if (zoom) zoom.disabled = note;
            instance.template.querySelectorAll('.pswp__item').forEach(holder => {
                holder.setAttribute('aria-hidden', String(!holder.contains(instance.currItem?.container)));
            });
            refreshCurrent();
        };
        // PhotoSwipe's drag and wheel handlers prevent native text selection and scrolling.
        // Keep those events native inside notes; navigation remains on the viewer controls.
        const keepNoteInteraction = event => {
            if (event.target.closest?.('.gallery-viewer-note')) event.stopPropagation();
        };
        const noteEvents = ['pointerdown', 'mousedown', 'touchstart', 'wheel'];
        noteEvents.forEach(name => instance.template.addEventListener(name, keepNoteInteraction, {capture: true, passive: true}));
        instance.listen('afterChange', updateItemControls);
        instance.listen('destroy', () => {
            noteEvents.forEach(name => instance.template.removeEventListener(name, keepNoteInteraction, true));
            instance.template.classList.remove('gallery-viewer-note-active');
            pending.forEach(image => { image.onload = image.onerror = null; });
            pending.clear();
        });
        return new PhotoSwipeUI(instance, framework);
    };
}

function CarouselImage({item, index, label, category, thumbnails, onOpen, dimensionsCache}) {
    const source = getGalleryImage(item);
    const original = useCdn(source, null, 1680);
    const thumbnail = useCdn(source, null, 480);
    const [dimensions, setDimensions] = useState({width: 1680, height: 1680});
    return <Item original={original} thumbnail={thumbnail} width={dimensions.width} height={dimensions.height}
        title={getCaption(item, category)}>
        {({ref}) => <button ref={ref} type="button" className="gallery-carousel-tile rounded-theme section-grey"
            aria-label={`Open ${item.title?.trim() || item.altText?.trim() || `${label} Image ${index + 1}`}`}
            onClick={event => onOpen(index, event)}>
            <img src={thumbnail} alt={item.altText || ''} loading="lazy"
                className={classNames('aspect-square w-full h-full object-center', thumbnails ? 'object-contain' : 'object-cover')}
                onLoad={event => {
                    const image = event.currentTarget;
                    const size = imageDimensions(image);
                    if (size) {
                        dimensionsCache.set(original, size);
                        setDimensions(size);
                    }
                }}/>
        </button>}
    </Item>;
}

function CarouselNote({item, index, label, category, onOpen}) {
    const html = `<div class="gallery-viewer-note" role="document" tabindex="0" aria-label="Written Gallery Entry">${getCaption(item, category)}</div>`;
    return <Item html={html} width={0} height={0} galleryNote>
        {({ref}) => <button ref={ref} type="button" className="gallery-carousel-note rounded-theme section-grey"
            aria-label={`Read ${item.title?.trim() || `${label} Entry ${index + 1}`}`}
            onClick={event => onOpen(index, event)}>
            <span>{item.title?.trim() || 'Written Entry'}</span>
            <span>View Details</span>
        </button>}
    </Item>;
}

function OverviewImage({item, index, label, thumbnails, selected, onOpen, dimensionsCache}) {
    const original = useCdn(getGalleryImage(item), null, 1680);
    const thumbnail = useCdn(getGalleryImage(item), null, 480);
    return <div className="min-w-0">
        <button type="button" data-item-index={index} aria-current={selected || undefined}
            aria-label={`Open ${item.title?.trim() || item.altText?.trim() || `${label} Image ${index + 1}`}`}
            onClick={onOpen} className="gallery-overview-tile">
            <span className="block aspect-square overflow-hidden rounded-theme section-grey">
                <img src={thumbnail} alt={item.altText || ''} loading="lazy"
                    onLoad={event => {
                        const size = imageDimensions(event.currentTarget);
                        if (size) dimensionsCache.set(original, size);
                    }}
                    className={classNames('w-full h-full object-center', thumbnails ? 'object-contain' : 'object-cover')}/>
            </span>
        </button>
        <Caption item={item} compact/>
    </div>;
}

function CarouselContents({items, categories, label, thumbnails, onViewerOpenRef, dimensionsCache}) {
    const {open} = useGallery();
    const context = useContext(WebsiteContext);
    const containerRef = useRef(null);
    const railRef = useRef(null);
    const [columns, setColumns] = useState(4);
    const [edges, setEdges] = useState({start: true, end: true});
    const overlay = useGalleryOverlaySession(containerRef, open);
    onViewerOpenRef.current = overlay.onViewerOpen;
    const imageOnly = items.every(getGalleryImage);
    const itemCount = items.length;
    const itemScope = JSON.stringify(items.map(item => [item.id, getGalleryImage(item)]));
    const previousScope = useRef(itemScope);

    useEffect(() => {
        if (previousScope.current !== itemScope) overlay.close();
        previousScope.current = itemScope;
    }, [itemScope, overlay.close]);

    const measure = useCallback(() => {
        const rail = railRef.current;
        if (!rail) return;
        setColumns(columnsForWidth(rail.clientWidth));
        const next = {start: rail.scrollLeft <= 1, end: rail.scrollWidth - rail.clientWidth - rail.scrollLeft <= 1};
        setEdges(previous => previous.start === next.start && previous.end === next.end ? previous : next);
    }, []);
    useEffect(() => {
        const rail = railRef.current;
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        if (rail) observer?.observe(rail);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, [measure, itemScope, columns]);

    const moveRail = direction => {
        const rail = railRef.current;
        if (!rail) return;
        const stride = (rail.firstElementChild?.getBoundingClientRect().width || 0) + parseFloat(getComputedStyle(rail).columnGap || 0);
        rail.scrollBy({left: direction * columns * stride, behavior: reducedMotion() ? 'auto' : 'smooth'});
    };
    const attachOverview = useCallback(node => {
        overlay.overviewRef.current = node;
        if (node) node.scrollTop = overlay.overviewScroll.current;
    }, [overlay.overviewRef, overlay.overviewScroll]);
    const countLabel = `${itemCount} ${imageOnly ? itemCount === 1 ? 'Image' : 'Images' : itemCount === 1 ? 'Item' : 'Items'}`;

    return <section ref={containerRef} tabIndex={-1} className="gallery-carousel" aria-label={`${label} Gallery`}>
        <div ref={railRef} className="gallery-carousel-rail" style={{'--gallery-columns': columns}}
            data-at-end={edges.end} aria-label={`${label} Items`} tabIndex={0} onScroll={measure}>
            {items.map((item, index) => {
                const category = categories.find(category => category.id === getGalleryCategoryId(item.category));
                const onOpen = context?.builder ? () => {} : (index, event) => overlay.showImage(index, event);
                return getGalleryImage(item)
                    ? <CarouselImage key={item.id || index} item={item} index={index} label={label}
                        category={category} thumbnails={thumbnails} dimensionsCache={dimensionsCache} onOpen={onOpen}/>
                    : <CarouselNote key={item.id || index} item={item} index={index} label={label}
                        category={category} onOpen={onOpen}/>;
            })}
        </div>
        <div className="gallery-carousel-footer">
            <button type="button" className="gallery-carousel-all" aria-label={`View All ${countLabel}`}
                onClick={context?.builder ? undefined : overlay.showOverview}>
                <ViewGridIcon aria-hidden="true"/><span>View All ({itemCount})</span>
            </button>
            <div className="gallery-carousel-arrows">
                <button type="button" aria-label={`Previous ${label} Items`} disabled={edges.start} onClick={() => moveRail(-1)}>
                    <ArrowNarrowLeftIcon aria-hidden="true"/>
                </button>
                <button type="button" aria-label={`Next ${label} Items`} disabled={edges.end} onClick={() => moveRail(1)}>
                    <ArrowNarrowRightIcon aria-hidden="true"/>
                </button>
            </div>
        </div>
        <ModalDialog isOpen={overlay.session.phase === 'overview'} onClose={overlay.close} motion="fade" restoreFocus={false}
            onAfterLeave={overlay.afterOverviewLeave} style={overlay.style} className="gallery-image-overview"
            containerClassName="gallery-overview-container" frameClassName="gallery-overview-frame"
            title={<>{label}<small className="block mt-1 text-xs font-normal leading-normal opacity-60">{countLabel}</small></>}
            titleClassName="shrink-0 break-words"
            panelClassName="gallery-overview-panel" bodyClassName="gallery-overview-body" closeButtonLabel="Close Image Overview"
            getInitialFocusElement={panel => panel.querySelector(`[data-item-index="${overlay.session.index || 0}"]`) || panel}>
            <div ref={attachOverview} className="gallery-overview-grid">
                {items.map((item, index) => {
                    return getGalleryImage(item) ? <OverviewImage key={item.id || index} item={item} index={index}
                        selected={index === overlay.session.index} label={label} thumbnails={thumbnails} dimensionsCache={dimensionsCache}
                        onOpen={event => overlay.showImage(index, event, true)}/>
                        : <button key={item.id || index} type="button" data-item-index={index}
                            aria-current={index === overlay.session.index || undefined}
                            aria-label={`Read ${item.title?.trim() || `${label} Entry ${index + 1}`}`}
                            onClick={event => overlay.showImage(index, event, true)}
                            className="gallery-overview-tile gallery-overview-note rounded-theme section-grey">
                            <div inert=""><Caption item={item} compact/></div>
                        </button>;
                })}
            </div>
        </ModalDialog>
    </section>;
}

export default function GalleryCarousel({items, categories, label = 'Gallery', thumbnails}) {
    const layoutRef = useRef(null);
    const onViewerOpenRef = useRef(null);
    const dimensionsCache = useRef(new Map()).current;
    const ui = useMemo(() => createGalleryUi(dimensionsCache), [dimensionsCache]);
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    return <CustomGallery layoutRef={layoutRef} ui={ui} options={{
        mainClass: 'gallery-carousel-photoswipe', shareEl: false, fullscreenEl: false, history: false, focus: false,
        closeOnScroll: false, showHideOpacity: true, showAnimationDuration: 0, hideAnimationDuration: typeof window !== 'undefined' && reducedMotion() ? 0 : 140,
        maxSpreadZoom: 4, barsSize: {top: 48, bottom: 'auto'},
        getDoubleTapZoom: (isMouseClick, item) => item.galleryNote ? 1 : isMouseClick ? 2 : item.initialZoomLevel < 0.7 ? 1 : 4
    }} onOpen={instance => onViewerOpenRef.current?.(instance)}>
        <CarouselContents {...{items, categories, label, thumbnails, onViewerOpenRef, dimensionsCache}}/>
        {mounted ? createPortal(<DefaultLayout ref={layoutRef} className="pswp gallery-carousel-photoswipe"/>, document.body) : null}
    </CustomGallery>;
}
